import { Controller } from "@hotwired/stimulus"
import { I18n } from "i18n-js"
import flatpickr from "flatpickr"
import { Portuguese } from "flatpickr/dist/l10n/pt.js"

// window.I18n.{locale,translations} are set by a <script> tag that the
// layout renders *after* the application/elements bundles — so reading them
// at module load time (this file's top level) always sees the pre-load
// defaults. Building the instance lazily, inside the method that needs it,
// reads window.I18n once it has actually been populated. `I18n` here is a
// class (i18n-js v4) — `t`/`p`/`l` are instance methods, so it needs to be
// instantiated rather than used as a ready-made singleton.
function buildI18n() {
  return new I18n(window.I18n?.translations || {}, {
    locale: window.I18n?.locale || "en"
  })
}

function currentFlatpickrLocale() {
  const locale = window.I18n?.locale || ""
  return locale.startsWith("pt") ? Portuguese : "default"
}

// Intl.DateTimeFormat wants a BCP-47 tag ("pt-BR"), i18n-js locales use an
// underscore ("pt_BR").
function currentIntlLocale() {
  return (window.I18n?.locale || "").replace("_", "-") || undefined
}

const UNITS = {
  m:  { seconds: 60 },
  h:  { seconds: 3600 },
  d:  { seconds: 86_400 },
  w:  { seconds: 604_800 },
  mo: { seconds: 2_592_000 }, // approximation: 30d
  y:  { seconds: 31_536_000 }
}

// "mo" before "m" so it doesn't get swallowed by the single-letter unit
const CUSTOM_RE = /^\s*(\d+)\s*(mo|m|h|d|w|y)\s*$/i

export default class extends Controller {
  static targets = [
    "trigger", "label", "panel", "kbdHint",
    "presetsView", "absoluteView",
    "customInput", "customError",
    "flatpickr", "flatpickrMount",
    "startTime", "endTime", "utc",
    "presetInput", "startInput", "endInput", "utcInput",
    "presetItem"
  ]
  static values = {
    mode: { type: String, default: "datetime" },
    direction: { type: String, default: "backward" }
  }

  connect() {
    this.fp = flatpickr(this.flatpickrTarget, {
      mode: "range",
      inline: true,
      enableTime: false,
      locale: currentFlatpickrLocale(),
      appendTo: this.flatpickrMountTarget,
      defaultDate: this.isForward
        ? [new Date(), new Date(Date.now() + 86_400e3)]
        : [new Date(Date.now() - 86_400e3), new Date()]
    })

    this.activeIndex = -1
    this.detectPlatform()

    this.outsideClick = (e) => {
      if (!this.element.contains(e.target)) this.close()
    }
    this.globalKeydown = this.handleGlobalKeydown.bind(this)
    this.panelKeydown = this.handlePanelKeydown.bind(this)

    document.addEventListener("click", this.outsideClick)
    document.addEventListener("keydown", this.globalKeydown)
    this.panelTarget.addEventListener("keydown", this.panelKeydown)
  }

  disconnect() {
    document.removeEventListener("click", this.outsideClick)
    document.removeEventListener("keydown", this.globalKeydown)
    this.panelTarget.removeEventListener("keydown", this.panelKeydown)
    this.fp?.destroy()
  }

  get isDatetime() { return this.modeValue === "datetime" }

  get isForward() { return this.directionValue === "forward" }

  detectPlatform() {
    const isMac = /Mac|iPhone|iPad/.test(navigator.platform)
    if (this.hasKbdHintTarget) {
      this.kbdHintTarget.textContent = isMac ? "⌘K" : "Ctrl+K"
    }
    this.modKey = isMac ? "metaKey" : "ctrlKey"
  }

  // --- Open/close ---

  open() {
    this.panelTarget.classList.remove("hidden")
    this.triggerTarget.setAttribute("aria-expanded", "true")
    this.setActive(-1)
  }

  close() {
    this.panelTarget.classList.add("hidden")
    this.triggerTarget.setAttribute("aria-expanded", "false")
    this.hideCustomError()
    this.setActive(-1)
    this.showPresets() // reset the view for next time it opens
  }

  togglePanel(e) {
    e.stopPropagation()
    this.panelTarget.classList.contains("hidden") ? this.open() : this.close()
  }

  // --- Global shortcuts ---

  handleGlobalKeydown(e) {
    if (e[this.modKey] && e.key.toLowerCase() === "k") {
      e.preventDefault()
      this.open()
      this.customInputTarget.focus()
      return
    }
    if (e.key === "Escape" && !this.panelTarget.classList.contains("hidden")) {
      e.preventDefault()
      this.close()
      this.triggerTarget.focus()
    }
  }

  // --- Keyboard navigation within the panel ---

  handlePanelKeydown(e) {
    if (this.presetsViewTarget.classList.contains("hidden")) return
    const items = this.presetItemTargets
    if (!items.length) return

    switch (e.key) {
      case "ArrowDown": e.preventDefault(); this.moveActive(1); break
      case "ArrowUp":   e.preventDefault(); this.moveActive(-1); break
      case "Home":      e.preventDefault(); this.setActive(0); break
      case "End":       e.preventDefault(); this.setActive(items.length - 1); break
      case "Enter":
        if (e.target === this.customInputTarget && this.customInputTarget.value.trim()) return
        if (this.activeIndex >= 0) { e.preventDefault(); items[this.activeIndex].click() }
        break
    }
  }

  setActive(index) {
    const items = this.presetItemTargets
    items.forEach(el => el.classList.remove("is-active"))
    this.activeIndex = index
    if (index >= 0 && items[index]) {
      items[index].classList.add("is-active")
      items[index].scrollIntoView({ block: "nearest" })
    }
  }

  moveActive(delta) {
    const items = this.presetItemTargets
    const next = (this.activeIndex + delta + items.length) % items.length
    this.setActive(next)
  }

  hoverPreset(e) {
    this.setActive(Number(e.currentTarget.dataset.index))
  }

  // --- Fixed presets ---

  pickPreset(e) {
    e.preventDefault()
    const { preset, seconds } = e.currentTarget.dataset
    this.applyRelativeRange({
      preset,
      seconds: Number(seconds),
      label: e.currentTarget.textContent.trim()
    })
  }

  // --- Custom range ---

  validateCustom() {
    const v = this.customInputTarget.value
    if (!v) return this.hideCustomError()
    CUSTOM_RE.test(v) ? this.hideCustomError() : this.showCustomError()
  }

  applyCustom(e) {
    e.preventDefault()
    const match = this.customInputTarget.value.match(CUSTOM_RE)
    if (!match) return this.showCustomError()

    const [, n, rawUnit] = match
    const unit = rawUnit.toLowerCase()
    const spec = UNITS[unit]
    const count = Number(n)
    const seconds = count * spec.seconds

    const unitsKey = this.isForward ? "units_forward" : "units"
    this.applyRelativeRange({
      preset: `${count}${unit}`,
      seconds,
      label: buildI18n().t(`date_range_picker.${unitsKey}.${unit}`, { count })
    })
    this.customInputTarget.value = ""
  }

  showCustomError() { this.customErrorTarget.classList.remove("hidden") }
  hideCustomError() { this.customErrorTarget.classList.add("hidden") }

  applyRelativeRange({ preset, seconds, label }) {
    const start = this.isForward ? new Date() : new Date(Date.now() - seconds * 1000)
    const end = this.isForward ? new Date(Date.now() + seconds * 1000) : new Date()

    this.presetInputTarget.value = preset
    this.startInputTarget.value = this.serialize(start)
    this.endInputTarget.value = this.serialize(end)
    this.labelTarget.textContent = label

    this.close()
    this.dispatch("change", { detail: { preset, start, end, mode: this.modeValue } })
  }

  // --- Absolute (calendar) ---

  showAbsolute() {
    this.presetsViewTarget.classList.add("hidden")
    this.absoluteViewTarget.classList.remove("hidden")
  }

  showPresets() {
    this.absoluteViewTarget.classList.add("hidden")
    this.presetsViewTarget.classList.remove("hidden")
  }

  applyAbsolute() {
    const [d1, d2] = this.fp.selectedDates
    if (!d1 || !d2) return

    let start, end, utc = false

    if (this.isDatetime) {
      const merge = (date, timeStr) => {
        const [h, m] = (timeStr || "00:00").split(":").map(Number)
        const out = new Date(date)
        out.setHours(h, m, 0, 0)
        return out
      }
      start = merge(d1, this.startTimeTarget.value)
      end   = merge(d2, this.endTimeTarget.value)
      utc   = this.utcTarget.checked
      this.utcInputTarget.value = utc ? "1" : "0"
    } else {
      start = new Date(d1); start.setHours(0, 0, 0, 0)
      end   = new Date(d2); end.setHours(23, 59, 59, 999)
    }

    this.presetInputTarget.value = "absolute"
    this.startInputTarget.value = this.serialize(start, utc)
    this.endInputTarget.value   = this.serialize(end, utc)
    this.labelTarget.textContent = this.formatRangeLabel(start, end)

    this.close()
    this.dispatch("change", {
      detail: { preset: "absolute", start, end, utc, mode: this.modeValue }
    })
  }

  // --- Helpers ---

  serialize(date, utc = false) {
    if (this.isDatetime) return utc ? date.toISOString() : date.toString()
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, "0")
    const d = String(date.getDate()).padStart(2, "0")
    return `${y}-${m}-${d}`
  }

  formatRangeLabel(start, end) {
    const opts = this.isDatetime
      ? { dateStyle: "short", timeStyle: "short" }
      : { dateStyle: "short" }
    return new Intl.DateTimeFormat(currentIntlLocale(), opts).formatRange(start, end)
  }
}
